import React, { useMemo, useState } from "react";

import {
	Badge,
	Button,
	InfoMessage,
	InputSearch,
	Spinner,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { subscriptionStatusLine } from "@/utils/USubscription";

import { PLAN_NAMES } from "@/constants/CPlans";
import { adminKeys } from "@/constants/CQueryKeys";

import { TAdminUser } from "@/types";

/**
 * The accounts tab: who is on what, and a link to the full picture of
 * one - granting a plan by hand lives there (AdminWorkspaceDetail). No
 * editing of anything else: names, roles and deletion stay with their
 * owners.
 */
export const AdminUsers: React.FC = () => {
	const [q, setQ] = useState("");
	const [page, setPage] = useState(1);
	const navigate = useNavigate();

	const { data, isLoading, error } = useQuery({
		queryKey: adminKeys.users(q, page),
		queryFn: () => AdminService.users(q, page),
		placeholderData: (previous) => previous,
	});

	const search = (value: string) => {
		setQ(value.trim());
		setPage(1);
	};

	const columns: TTableColumn<TAdminUser>[] = useMemo(
		() => [
			{
				key: "lastName",
				label: t("admin.users.columns.name"),
				width: "1fr",
				render: (item) => (
					<span>
						<span className="font-medium">
							{item.firstName} {item.lastName}
						</span>
						<span className="ml-1 text-neutral-500">
							@{item.username}
						</span>
						{item.roles.includes("ROLE_ADMIN") && (
							<Badge
								color="neutral"
								variant="outline"
								size="sm"
								className="ml-2"
							>
								admin
							</Badge>
						)}
					</span>
				),
			},
			{
				key: "mail",
				label: t("admin.users.columns.mail"),
				width: "1fr",
			},
			{
				key: "userSince",
				label: t("admin.users.columns.since"),
				render: (item) =>
					format(new Date(item.userSince), "dd/MM/yyyy"),
			},
			{
				key: "plan",
				label: t("admin.users.columns.plan"),
				truncate: false,
				render: (item) => {
					const status = subscriptionStatusLine(
						item.subscription,
						item.plan
					);
					return (
						<span className="flex flex-wrap items-center gap-2">
							<Badge color="primary" variant="tint" size="sm">
								{PLAN_NAMES[item.plan]}
							</Badge>
							<Badge
								color={status.color}
								variant="tint"
								size="sm"
								title={status.text}
							>
								{t(`admin.users.status.${statusKey(item)}`)}
							</Badge>
							{item.subscription?.hasCustomer && (
								<Badge color="info" variant="outline" size="sm">
									Stripe
								</Badge>
							)}
						</span>
					);
				},
			},
		],
		[]
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center gap-3">
				<InputSearch
					placeholder={t("admin.users.search")}
					onChange={search}
					className="max-w-md flex-1"
					aria-label={t("admin.users.search")}
				/>
				{data && (
					<span
						className="text-sm text-neutral-500"
						data-testid="admin-users-total"
					>
						{t("admin.users.total", { count: data.total })}
					</span>
				)}
			</div>
			{isLoading && <Spinner />}
			{!isLoading && !!error && (
				<InfoMessage color="danger">{t("admin.error")}</InfoMessage>
			)}
			{!isLoading && !error && (
				<Table
					items={data?.data ?? []}
					getItemKey={(item) => item.id}
					columns={columns}
					noDataMessage={t("admin.users.empty")}
					onRowClick={(item) => {
						// The billing lives on the workspace that is them.
						navigate(`/admin/workspaces/${item.workspaceId}`);
					}}
				/>
			)}
			{!!data && data.pages > 1 && (
				<div className="flex items-center justify-end gap-3 text-sm">
					<Button
						size="sm"
						variant="outline"
						color="neutral"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
					>
						{t("admin.users.previous")}
					</Button>
					<span data-testid="admin-users-page">
						{t("admin.users.page", {
							page: data.page,
							pages: data.pages,
						})}
					</span>
					<Button
						size="sm"
						variant="outline"
						color="neutral"
						disabled={page >= data.pages}
						onClick={() => setPage((p) => p + 1)}
					>
						{t("admin.users.next")}
					</Button>
				</div>
			)}
		</div>
	);
};

/** One word for the row: the full sentence is on hover. */
const statusKey = (item: TAdminUser): string => {
	if (item.plan === "enterprise") return "manual";
	if (!item.subscription) return "free";
	if (
		item.subscription.status === "trialing" &&
		!item.subscription.hasCustomer
	)
		return "trial";
	if (item.subscription.status === "active" && !item.subscription.hasCustomer)
		return "manual";
	return item.subscription.status;
};
